/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DirtyType, ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import {
  forceUpdatedDirtyType,
  setEntityField,
  setNewDirtyType,
  setUpdatedDirtyType,
  whenNew,
  whenRemoved,
} from '../entity/entity.utils';

/**
 * Deletes row if it is marked as <code>New</code>, otherwise marks it as <code>Removed</code>.
 * This function can be used as merging function for {@link Table}s.
 */
export function removeDirtyRow<T extends WithDirtyType<E>, E>(row: T): Maybe<T> {
  if (whenNew(row)) {
    return void 0;
  } else {
    return { ...<any>row, dirtyType: DirtyType.Removed };
  }
}

/**
 * Marks row as <code>Updated</code> if it is marked as <code>Removed</code>,
 * otherwise marks it as <code>New</code>.
 *
 * This function can be used as merging function for {@link Table}s.
 */
export function newDirtyRow<T extends WithDirtyType<E>, E>(oldRow: Maybe<T>, newRow: T): T {
  if (oldRow) {
    return whenRemoved(oldRow) ? forceUpdatedDirtyType(newRow) : setUpdatedDirtyType(newRow);
  } else {
    return setNewDirtyType(newRow);
  }
}

/**
 * Marks row as <code>Updated</code> if it is marked as <code>Removed</code>,
 * otherwise marks it as <code>New</code>.
 *
 * If old row contains version field this function copies it into new row
 *
 * This function can be used as merging function for {@link Table}s.
 */
export function newDirtyRowWithVersion<T extends WithVersion<WithDirtyType<E>>, E>(oldRow: Maybe<T>, newRow: T): T {
  return newDirtyRow(oldRow, oldRow ? setEntityField(newRow, 'version', oldRow.version) : newRow);
}

export function createUniqueSortBy<T>(getId: TransformFn<T, ID>, sortBy: TransformFn<T, any>): TransformFn<T, any> {
  return (value: T) => `${sortBy(value)}\uffff${getId(value)}`;
}
